/**
 * The coin shower: real coin meshes with ballistics, not sprites.
 *
 * SPIELHÖLLE's whole promise is that money is physical, so a payout may never be a
 * particle puff. One InstancedMesh + one ink hull = 2 draws for every coin in the air,
 * including the Bank-sprengen money shot.
 */

import {
  InstancedMesh, CylinderGeometry, InstancedBufferAttribute, Matrix4, Quaternion, Vector3,
  Color, Euler, type Group, type Camera,
} from 'three';
import type { Material as MaterialName } from '../../../shared/contract';
import { MATERIALS } from '../art/palette';
import { toon } from '../art/toon';
import { inkOutlineInstanced, computeHullNormals } from '../art/outline';

interface Coin {
  alive: boolean;
  p: Vector3; v: Vector3;
  spin: Euler; spinV: Euler;
  t: number; life: number;
  scale: number;
  tint: Color;
  bounced: number;
  floorY: number;
}

const _m = new Matrix4();
const _q = new Quaternion();
const _e = new Euler();
const _s = new Vector3();

export interface CoinShowerOpts {
  capacity?: number;
  gravity?: number;
}

export class CoinShower {
  mesh: InstancedMesh;
  ink: InstancedMesh;
  private coins: Coin[] = [];
  private aTint: InstancedBufferAttribute;
  private aEmi: InstancedBufferAttribute;
  private aWidth: InstancedBufferAttribute;
  private gravity: number;
  private cap: number;

  constructor(opts: CoinShowerOpts = {}) {
    this.cap = opts.capacity ?? 260;
    this.gravity = opts.gravity ?? 46;

    const geo = new CylinderGeometry(0.5, 0.5, 0.17, 16);
    geo.rotateX(Math.PI / 2);
    computeHullNormals(geo);

    const mat = toon({
      color: 0xffffff, ramp: 'METAL4', rim: 0.6, halftone: 0,
      instanced: true, name: 'coinshower',
    });
    this.mesh = new InstancedMesh(geo, mat, this.cap);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = false;
    this.mesh.userData.noInk = true;

    const mk = (n: number, fill: number) =>
      new InstancedBufferAttribute(new Float32Array(this.cap * n).fill(fill), n);
    this.aTint = mk(3, 1); this.aEmi = mk(3, 0); this.aWidth = mk(1, 1);
    const pip = mk(3, 0), tag = mk(1, 0), phase = mk(1, 0);
    this.mesh.geometry = geo.clone();
    this.mesh.geometry.setAttribute('aTint', this.aTint);
    this.mesh.geometry.setAttribute('aEmi', this.aEmi);
    this.mesh.geometry.setAttribute('aPip', pip);
    this.mesh.geometry.setAttribute('aTag', tag);
    this.mesh.geometry.setAttribute('aPhase', phase);
    this.mesh.geometry.setAttribute('aWidth', this.aWidth);
    computeHullNormals(this.mesh.geometry);

    this.ink = inkOutlineInstanced(this.mesh, { widthPx: 1.6, body: MATERIALS.kupfer.base, instanced: true });
    this.ink.geometry = this.mesh.geometry;

    for (let i = 0; i < this.cap; i++) {
      this.coins.push({
        alive: false, p: new Vector3(), v: new Vector3(),
        spin: new Euler(), spinV: new Euler(), t: 0, life: 2, scale: 1,
        tint: new Color(1, 1, 1), bounced: 0, floorY: 0,
      });
    }
  }

  addTo(parent: Group): this { parent.add(this.ink, this.mesh); return this; }

  get live(): number { return this.mesh.count; }

  /** one coin, launched */
  spawn(pos: Vector3 | [number, number, number], vel: Vector3 | [number, number, number], opts: {
    material?: MaterialName; life?: number; scale?: number; floorY?: number;
  } = {}): void {
    const c = this.coins.find((k) => !k.alive);
    if (!c) return;
    const p = Array.isArray(pos) ? pos : [pos.x, pos.y, pos.z];
    const v = Array.isArray(vel) ? vel : [vel.x, vel.y, vel.z];
    c.alive = true;
    c.p.set(p[0], p[1], p[2]);
    c.v.set(v[0], v[1], v[2]);
    c.spin.set(Math.random() * 6, Math.random() * 6, Math.random() * 6);
    c.spinV.set((Math.random() - 0.5) * 22, (Math.random() - 0.5) * 22, (Math.random() - 0.5) * 12);
    c.t = 0;
    c.life = opts.life ?? 2.2;
    c.scale = opts.scale ?? 1;
    c.bounced = 0;
    c.floorY = opts.floorY ?? 0.3;
    c.tint.set(MATERIALS[opts.material ?? 'kupfer'].base);
  }

  /** an arc of coins out of a mouth — the standard payout gesture */
  fountain(from: Vector3, dir: Vector3, count: number, opts: {
    spread?: number; power?: number; material?: MaterialName; floorY?: number; life?: number;
  } = {}): void {
    const spread = opts.spread ?? 0.5;
    const power = opts.power ?? 22;
    const mats: MaterialName[] = ['kupfer', 'kupfer', 'silber', 'gold'];
    for (let i = 0; i < count; i++) {
      const a = (Math.random() - 0.5) * spread;
      const b = (Math.random() - 0.5) * spread;
      const v = new Vector3(dir.x + a, dir.y + Math.random() * 0.5, dir.z + b)
        .normalize().multiplyScalar(power * (0.65 + Math.random() * 0.6));
      this.spawn(
        [from.x + (Math.random() - 0.5) * 1.4, from.y + (Math.random() - 0.5) * 0.8, from.z + (Math.random() - 0.5) * 1.0],
        v,
        { material: opts.material ?? mats[Math.floor(Math.random() * mats.length)], floorY: opts.floorY, life: opts.life },
      );
    }
  }

  clear(): void {
    for (const c of this.coins) c.alive = false;
    this.mesh.count = 0;
    this.ink.count = 0;
  }

  update(dt: number, _camera?: Camera): void {
    let n = 0;
    for (const c of this.coins) {
      if (!c.alive) continue;
      c.t += dt;
      if (c.t >= c.life) { c.alive = false; continue; }
      c.v.y -= this.gravity * dt;
      c.p.addScaledVector(c.v, dt);
      if (c.p.y < c.floorY && c.v.y < 0) {
        c.p.y = c.floorY;
        c.v.y *= -0.34;
        c.v.x *= 0.7; c.v.z *= 0.7;
        c.spinV.x *= 0.5; c.spinV.y *= 0.5; c.spinV.z *= 0.5;
        c.bounced++;
        if (c.bounced > 3) { c.v.set(0, 0, 0); c.spinV.set(0, 0, 0); }
      }
      c.spin.x += c.spinV.x * dt;
      c.spin.y += c.spinV.y * dt;
      c.spin.z += c.spinV.z * dt;

      if (n >= this.cap) break;
      const fade = c.t > c.life - 0.35 ? (c.life - c.t) / 0.35 : 1;
      _e.set(c.spin.x, c.spin.y, c.spin.z);
      _q.setFromEuler(_e);
      _s.setScalar(c.scale * fade);
      _m.compose(c.p, _q, _s);
      this.mesh.setMatrixAt(n, _m);
      this.aTint.setXYZ(n, c.tint.r, c.tint.g, c.tint.b);
      this.aEmi.setXYZ(n, 0, 0, 0);
      this.aWidth.setX(n, 1);
      n++;
    }
    this.mesh.count = n;
    this.ink.count = n;
    if (n > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.aTint.needsUpdate = true;
      this.aEmi.needsUpdate = true;
      this.aWidth.needsUpdate = true;
    }
  }
}
